import _defineProperty from "/Users/matt/dev/github.com/industriousapps/excelkits-client/node_modules/.pnpm/next@13.5.7_@babel+core@7.25.2_react-dom@18.3.1_react@18.3.1__react@18.3.1_sass@1.79.3/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/defineProperty.js";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import merge from 'lodash/merge';
import omit from 'lodash/omit';
import uniq from 'lodash/uniq';
import set from 'lodash/set';
import { ThemeMode } from '@/interfaces/widgetTheme';
import { defaultThemeOptions } from '@/config/widgetTheme';
import { getObjectDiff } from '@/utils/objects';
import { cleanPalettes } from './utils';
// Resolve foundational properties like padding, radius, and typography early.
// This way, elements that reference these core values can use their final,
// resolved state during their own calculations.
const COMPUTABLE_FIELDS = ['base', 'spacing', 'color', 'colorPallets', 'typography', 'elements'];
const BASE_FIELDS = ['id', 'name', 'fonts', 'shared'];
const META_FIELDS = ['slug', 'consumerWidgets', 'createdAt', 'updatedAt', 'team'];
const DERIVED_FIELDS = ['color', 'colorPallets', 'spacing', 'typography', 'elements'];
export const createTheme = settings => {
  const {
    baseOptions = defaultThemeOptions,
    overrides,
    computeVariants = true
  } = settings || {}; // Compute "light" theme from base options and overrides.

  const computedTheme = computeThemeFromOptions(baseOptions, overrides); // Return early if no variants are needed.

  if (!computeVariants) {
    return computedTheme;
  }

  computedTheme.variants = {};
  const baseVariants = baseOptions.variants || {};
  const overrideVariants = (overrides === null || overrides === void 0 ? void 0 : overrides.variants) || {};
  const allVariantModes = uniq([ThemeMode.Dark, ...Object.keys(baseVariants), ...Object.keys(overrideVariants)]);
  allVariantModes.forEach(variantMode => {
    const variantTheme = createVariantTheme(computedTheme, baseVariants[variantMode] || {}, overrideVariants[variantMode] || {});

    if (Object.keys(variantTheme).length > 0) {
      set(computedTheme, `variants.${variantMode}`, variantTheme);
    }
  });
  return computedTheme;
};

const createVariantTheme = (baseLightTheme, baseVariantValues, variantOverrides) => {
  const result = omit(_objectSpread({}, baseLightTheme), [...BASE_FIELDS, ...META_FIELDS, 'variants']);
  DERIVED_FIELDS.forEach(field => {
    const lightValue = getComputedValue(baseLightTheme[field], result);
    const variantValue = getComputedValue(baseVariantValues[field], result);
    const overrideValue = variantOverrides !== null && variantOverrides !== void 0 && variantOverrides[field] ? getComputedValue(variantOverrides[field], result) : undefined;
    const mergedValue = mergeThemeValues(lightValue, variantValue, overrideValue);

    if (field === 'colorPallets') {
      result[field] = cleanPalettes(mergedValue);
    } else {
      result[field] = mergedValue;
    }
  });
  return result;
};

const computeThemeFromOptions = (baseTheme, options) => {
  const computedTheme = _objectSpread({}, baseTheme); // Merge base theme data with options


  BASE_FIELDS.forEach(field => {
    computedTheme[field] = (options === null || options === void 0 ? void 0 : options[field]) ?? computedTheme[field];
  }); // We pass the `computedTheme` itself as the second argument to `getComputedValue`.
  // This allows derived values ('({ theme }) => theme.colors.primary') to resolve
  // against other values being computed in the same pass.

  COMPUTABLE_FIELDS.forEach(field => {
    const computedValue = computeThemeField(computedTheme, field, options);

    if (field === 'colorPallets') {
      computedTheme[field] = cleanPalettes(computedValue);
    } else {
      computedTheme[field] = computedValue;
    }
  });
  return computedTheme;
}; // Safely merges values, handling arrays separately from objects


const mergeThemeValues = function () {
  for (var _len = arguments.length, values = new Array(_len), _key = 0; _key < _len; _key++) {
    values[_key] = arguments[_key];
  }

  const validValues = values.filter(Boolean);

  if (validValues.length === 0) {
    return {};
  } // If any value is an array, return the last non-undefined array


  if (Array.isArray(validValues[0])) {
    return validValues[validValues.length - 1];
  }

  return merge({}, ...validValues);
}; // Computes a theme value that can be either a direct value or a function


const getComputedValue = (valueOrFn, theme) => typeof valueOrFn === 'function' ? valueOrFn({
  theme
}) : valueOrFn; // Computes a single theme field value


export const computeThemeField = (theme, field, options, diffOnly) => {
  const defaultValue = getComputedValue(defaultThemeOptions[field], theme);
  const themeValue = getComputedValue(theme[field], theme);
  const overrideValue = options !== null && options !== void 0 && options[field] ? getComputedValue(options[field], theme) : undefined;

  if (!diffOnly) {
    return mergeThemeValues(defaultValue, themeValue, overrideValue);
  }

  return getObjectDiff(overrideValue, themeValue);
}; // Compute theme differences from a reference theme

export const computeThemeDiff = (sourceTheme, referenceTheme) => {
  const diff = {};
  COMPUTABLE_FIELDS.forEach(field => {
    const sourcValue = sourceTheme[field];
    const refValue = referenceTheme[field];
    const fieldDiff = getObjectDiff(sourcValue, refValue, true);

    if (fieldDiff && Object.keys(fieldDiff).length > 0) {
      diff[field] = fieldDiff;
    }
  });
  return diff;
};